import { useCallback, useEffect, useRef, useState } from "react";
import { useSaveUserInfo } from "@dealer-center/ddc-pages-configuration-fetch";
import type { UserDetail } from "@dealer-center/ddc-pages-configuration-interface";
import type { UserInfoTabId } from "../useUserInfoTabs";
import { saveErrorMessage } from "../writeErrorMessages";
import { USER_INFO_FIELD_IDS } from "./fieldIds";
import type { UserInfoEdits, UserInfoFieldName } from "./useUserInfoEdits";
import {
  type UserInfoFieldMessages,
  firstInvalidUserInfoField,
  userInfoFieldErrorMessages,
  validateUserInfo
} from "./validateUserInfo";

export interface UseUserInfoSaveOptions {
  /** The subject being edited. A save's outcome applies only while it is still this subject. */
  userId: string | undefined;
  accountId: string | undefined;
  /** The stored record the edits are compared against. */
  summary: UserDetail | undefined;
  edits: UserInfoEdits;
  activeTabId: UserInfoTabId;
  selectTab: (tabId: UserInfoTabId) => void;
  /** Selects the failure copy's register: first person only when the subject is the operator. */
  isSubjectTheOperator?: boolean;
}

/**
 * The profile save, shared by both pages: validation, the failure message, focusing the first
 * invalid field, and the in-flight guard. The page owns the edits and the record; this owns the
 * save. Success is announced by the page's header actions, which sit inside `StatusRegion`.
 */
export const useUserInfoSave = ({
  userId,
  accountId,
  summary,
  edits,
  activeTabId,
  selectTab,
  isSubjectTheOperator = false
}: UseUserInfoSaveOptions) => {
  const saveUserInfo = useSaveUserInfo();
  const [saveFailureMessage, setSaveFailureMessage] = useState<
    string | undefined
  >();
  const [isShowingFieldErrors, setIsShowingFieldErrors] = useState(false);
  const [fieldToFocus, setFieldToFocus] = useState<
    UserInfoFieldName | undefined
  >();

  // Derived once for Save and the leave-the-page guard, so the two never disagree about edits.
  const isFormDirty = edits.isDirty(summary);

  // Always an object: passing `errors` at all opts the fields into validation.
  const fieldErrorMessages: UserInfoFieldMessages = isShowingFieldErrors
    ? userInfoFieldErrorMessages(validateUserInfo(edits.changedFields(summary)))
    : {};

  // A failure is about edits that are no longer pending once the form is clean.
  useEffect(() => {
    if (!isFormDirty) {
      setSaveFailureMessage(undefined);
    }
  }, [isFormDirty]);

  // Waits for User Information to be active: an inactive tab's field is not in the document.
  useEffect(() => {
    if (!fieldToFocus || activeTabId !== "userinfo") {
      return;
    }

    document.getElementById(USER_INFO_FIELD_IDS[fieldToFocus])?.focus();
    setFieldToFocus(undefined);
  }, [fieldToFocus, activeTabId]);

  // A write can resolve after another user was selected; its outcome must not apply to them.
  const userIdRef = useRef(userId);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // A ref, not `isPending`: set synchronously, so a double click cannot send the save twice.
  const isSaveInFlightRef = useRef(false);

  /** Resolves `true` only when a write landed for the still-current subject. */
  const handleSave = async (): Promise<boolean> => {
    if (isSaveInFlightRef.current || !userId || !accountId || !summary) {
      return false;
    }

    const fields = edits.changedFields(summary);
    if (Object.keys(fields).length === 0) {
      edits.reset();
      return false;
    }

    const firstInvalidField = firstInvalidUserInfoField(
      validateUserInfo(fields)
    );
    if (firstInvalidField) {
      setIsShowingFieldErrors(true);
      selectTab("userinfo");
      setFieldToFocus(firstInvalidField);
      return false;
    }

    const savedUserId = userId;
    isSaveInFlightRef.current = true;
    setSaveFailureMessage(undefined);
    try {
      await saveUserInfo.mutateAsync({
        userId: savedUserId,
        accountId,
        fields
      });
    } catch (error) {
      if (userIdRef.current === savedUserId) {
        setSaveFailureMessage(
          saveErrorMessage(error, { isSubjectTheOperator })
        );
      }
      return false;
    } finally {
      isSaveInFlightRef.current = false;
    }

    if (userIdRef.current !== savedUserId) {
      return false;
    }

    edits.reset();
    setIsShowingFieldErrors(false);
    return true;
  };

  /** Save state is per subject: call this wherever the subject changes. */
  const clearSaveState = useCallback(() => {
    setSaveFailureMessage(undefined);
    setIsShowingFieldErrors(false);
    setFieldToFocus(undefined);
  }, []);

  return {
    handleSave,
    clearSaveState,
    isFormDirty,
    isSaving: saveUserInfo.isPending,
    saveFailureMessage,
    fieldErrorMessages
  };
};
